import {
  AtlasGqlDispatchMode,
  AtlasGqlTask,
  useSubscribeToNotificationsForWorkContainerMutation,
  useWorkContainerNotificationSubscriptionSettingsQuery,
} from 'types/atlas-graphql';
import { NotificationSubscriptionButton } from './NotificationSubscriptionButton';
import { TooltipWithQuestionIcon, useMessage, useNotification } from 'components/ui';
import { useFeatures } from 'utils/features';
import {
  getParentCampaignLink,
  getParentWorkOrderLink,
  isParentCampaignSubscribed,
  isParentWorkOrderSubscribed,
} from './utils';
import { useCallback, useRef } from 'react';
import { ButtonContainer, CheckboxLabel, PromptContainer } from './NotificationSubscriptions.style';
import { startCase } from 'lodash';
import { BulkNotificationSubscriptionButton } from './BulkNotificationSubscriptionButton';
import { getItem as getLocalStorageItem, setItem as setLocalStorageItem } from 'utils/storage';
import { Checkbox } from 'antd5';
import { DeclineSubscriptionButton } from './DeclineSubscriptionButton';

export const autoWCNSubscriptionStorageKey = 'auto-subscribe-work-container-notifications';

type PromptParams = {
  content: JSX.Element;
  workContainer: Partial<AtlasGqlTask> & { __typename?: string };
  containerIdsForBulkSubscription?: string[];
};

type Result = {
  openPrompt: (params: PromptParams) => void;
};

export const useWCNotificationSubscriptionPrompt = (): Result => {
  const { WORK_CONTAINER_NOTIFICATIONS } = useFeatures().features;

  const saveSubscriptionSelection = useRef<boolean>(false);

  const notification = useNotification();
  const message = useMessage();

  // TODO: to support in-app notifications, duplicate this query with AtlasGqlDispatchMode.InApp and treat with an "or"
  const { data: workContainerEmailSubscriptionSettingsData } =
    useWorkContainerNotificationSubscriptionSettingsQuery({
      variables: { input: { dispatchMode: AtlasGqlDispatchMode.Email } },
      skip: !WORK_CONTAINER_NOTIFICATIONS,
    });
  const notificationsEnabled =
    !!workContainerEmailSubscriptionSettingsData?.workContainerNotificationSubscriptionSettings
      .enabled;

  const [subscribe] = useSubscribeToNotificationsForWorkContainerMutation({});

  /**
   * If the user has notifications enabled, has the release toggle, and is not already
   * subscribed to a parent container, prompt them to subscribe to notifications
   * Otherwise, just display the success message.
   */
  const openPrompt = useCallback(
    ({ content, workContainer, containerIdsForBulkSubscription }: PromptParams) => {
      if (WORK_CONTAINER_NOTIFICATIONS && notificationsEnabled) {
        const handleDeclineSubscription = (notificationKey: string) => {
          if (saveSubscriptionSelection.current) {
            setLocalStorageItem(autoWCNSubscriptionStorageKey, 'false');
          }
          setTimeout(() => {
            notification.destroy(notificationKey);
          }, 2000);
        };

        const afterSubscribe = (notificationKey: string) => {
          if (saveSubscriptionSelection.current) {
            setLocalStorageItem(autoWCNSubscriptionStorageKey, 'true');
          }
          setTimeout(() => {
            notification.destroy(notificationKey);
          }, 2000);
        };

        const shouldAutoSubscribe = getLocalStorageItem(autoWCNSubscriptionStorageKey) === 'true';
        const shouldSkipSubscribeOption =
          getLocalStorageItem(autoWCNSubscriptionStorageKey) === 'false';

        const parentCampaignSubscribed = isParentCampaignSubscribed(workContainer);
        const parentWorkOrderSubscribed = isParentWorkOrderSubscribed(workContainer);

        const automaticSubscriptionContent = (
          <>
            You were automatically subscribed to Notifications{' '}
            <TooltipWithQuestionIcon
              message={
                <>
                  You previously opted into automatic Subscriptions. This can be changed in{' '}
                  <a href="/settings/notification-settings" target="_blank">
                    settings
                  </a>{' '}
                  any time
                </>
              }
            />
          </>
        );

        if (shouldSkipSubscribeOption) {
          message.success(content);
        } else if (containerIdsForBulkSubscription) {
          if (shouldAutoSubscribe) {
            containerIdsForBulkSubscription.map(containerId =>
              subscribe({ variables: { input: { containerId } } })
            );
            notification.success({
              key: 'bulkTaskCreationSuccess',
              message: null,
              description: (
                <PromptContainer>
                  {content}
                  <br />
                  {automaticSubscriptionContent}
                </PromptContainer>
              ),
              placement: 'top',
            });
          } else {
            notification.success({
              key: 'bulkTaskCreationSuccess',
              message: null,
              description:
                parentCampaignSubscribed || parentWorkOrderSubscribed ? (
                  <PromptContainer>
                    {content}
                    <br />
                    <>
                      You are subscribed to Notifications for{' '}
                      {parentCampaignSubscribed
                        ? getParentCampaignLink(workContainer, true)
                        : getParentWorkOrderLink(workContainer, true)}
                      , which these Tasks are in, and will receive Notifications about changes.
                    </>
                  </PromptContainer>
                ) : (
                  <>
                    <PromptContainer>
                      {content}
                      <br />
                      <>
                        Would you like to subscribe to Notifications for{' '}
                        {containerIdsForBulkSubscription.length === 1
                          ? 'this Task'
                          : 'each of these Tasks'}
                        ?
                      </>
                    </PromptContainer>
                    <Checkbox
                      defaultChecked={saveSubscriptionSelection.current}
                      onChange={e => {
                        saveSubscriptionSelection.current = e.target.checked;
                      }}
                      name="Remember my selection"
                    >
                      <CheckboxLabel>Remember my selection </CheckboxLabel>
                      <TooltipWithQuestionIcon
                        message={
                          <>
                            When you create work containers in the future, your subscription
                            preference will be applied. This can be changed in{' '}
                            <a href="/settings/notification-settings" target="_blank">
                              settings
                            </a>{' '}
                            any time
                          </>
                        }
                      />
                    </Checkbox>
                    <ButtonContainer>
                      <DeclineSubscriptionButton
                        onClick={() => handleDeclineSubscription('bulkTaskCreationSuccess')}
                      />
                      <BulkNotificationSubscriptionButton
                        containerIds={containerIdsForBulkSubscription}
                        afterSubscribe={() => afterSubscribe('bulkTaskCreationSuccess')}
                      />
                    </ButtonContainer>
                  </>
                ),
              placement: 'top',
              duration: 8,
            });
          }
        } else {
          if (shouldAutoSubscribe) {
            subscribe({ variables: { input: { containerId: workContainer.id } } });
            notification.success({
              key: workContainer.id,
              message: null,
              description: (
                <PromptContainer>
                  {content}
                  <br />
                  {automaticSubscriptionContent}
                </PromptContainer>
              ),
              placement: 'top',
            });
          } else {
            const workContainerTypeName =
              workContainer.type?.name ?? startCase(workContainer.__typename) ?? 'Work Container';

            notification.success({
              key: workContainer.id,
              message: null,
              description:
                parentCampaignSubscribed || parentWorkOrderSubscribed ? (
                  <PromptContainer>
                    {content}
                    <br />
                    <>
                      You are subscribed to Notifications for{' '}
                      {parentCampaignSubscribed
                        ? getParentCampaignLink(workContainer, true)
                        : getParentWorkOrderLink(workContainer, true)}
                      , which this {workContainerTypeName} is in, and will receive Notifications
                      about changes.
                    </>
                  </PromptContainer>
                ) : (
                  <>
                    <PromptContainer>
                      {content}
                      <br />
                      <>
                        Would you like to subscribe to Notifications for this{' '}
                        {workContainerTypeName}?
                      </>
                    </PromptContainer>
                    <Checkbox
                      data-testid="subscription-remember-selection"
                      defaultChecked={saveSubscriptionSelection.current}
                      onChange={e => {
                        saveSubscriptionSelection.current = e.target.checked;
                      }}
                      name="Remember my selection"
                    >
                      <CheckboxLabel>Remember my selection </CheckboxLabel>
                      <TooltipWithQuestionIcon
                        message={
                          <>
                            When you create work containers in the future, your subscription
                            preference will be applied. This can be changed in{' '}
                            <a href="/settings/notification-settings" target="_blank">
                              settings
                            </a>{' '}
                            any time
                          </>
                        }
                      />
                    </Checkbox>
                    <ButtonContainer>
                      <DeclineSubscriptionButton
                        onClick={() => handleDeclineSubscription(workContainer.id)}
                      />
                      <NotificationSubscriptionButton
                        containerId={workContainer.id}
                        afterSubscribe={() => afterSubscribe(workContainer.id)}
                      />
                    </ButtonContainer>
                  </>
                ),
              placement: 'top',
              duration: 8,
            });
          }
        }
      } else {
        message.success(content);
      }
    },
    [WORK_CONTAINER_NOTIFICATIONS, notificationsEnabled, saveSubscriptionSelection.current]
  );

  return {
    openPrompt,
  };
};
